import React from 'react';
import { Fade } from 'framework/ui/mui-builder';
import { useListContext } from 'framework/list';
import { TimelineContextData, TimelineItem } from 'framework/ui/timeline';
import { ResourceData } from 'framework/crud';
import { useShowContext } from 'framework/show';
import Box from '@mui/material/Box';

export interface AddTimelineItemInputProps {
  registries: any[];
  selectItemCallback: (registry: any) => void;
}

interface PersonTimelinePageLayoutProps {
  children?: React.ReactNode;
  filter?: React.ReactNode;
  hasAddForm?: boolean;
  additionalButtons?: React.ReactNode[];
  menu?: React.ReactNode;
  breadcrumbs?: React.ReactNode;
  entityHeader?: React.ReactNode;
  messageContext: string;
  timelineHeader?: React.ReactNode;
  AddTimelineItemInput?: React.ComponentType<AddTimelineItemInputProps>;
}

const DEFAULT_FUNCTION = () => {};

const PersonTimelinePageLayout = ({
  children,
  filter,
  timelineHeader = null,
  hasAddForm = true,
  additionalButtons,
  messageContext,
  AddTimelineItemInput,
}: PersonTimelinePageLayoutProps) => {
  const {
    addForm,
    addToList = DEFAULT_FUNCTION,
    createTimelineItemMethod = DEFAULT_FUNCTION,
    setAddForm = DEFAULT_FUNCTION,
    isEntryViewMode,
    reloadData,
    wrapItem = () => ({}),
    registries = [],
  }: TimelineContextData = useListContext();
  const { data: parentContext } = useShowContext();

  const closeAddForm = () => setAddForm(null);

  return (
    <>
      <Box
        className="person_timeline__page_top"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', height: '60px' }}
      >
        {timelineHeader}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
            gap: 1,
          }}
        >
          <Fade in={!!filter} timeout={20}>
            <Box
              className="person_timeline__filter_icon"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '1rem',
                '& > *': { m: 0 },
              }}
            >
              {isEntryViewMode ? null : filter}
            </Box>
          </Fade>
          <Box
            className="person_timeline__page_top_block"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1.5,
              height: '40px',
              '& > *': { height: '36px' },
            }}
          >
            {hasAddForm && !addForm && AddTimelineItemInput && (
              <AddTimelineItemInput
                registries={registries}
                selectItemCallback={registry => {
                  const { AddForm, source, urlPostfix, additionalAfterAddSubmit } = registry;

                  const form = (
                    <AddForm
                      messageContext={messageContext}
                      onCancel={closeAddForm}
                      submitFormFunc={(data: ResourceData) => createTimelineItemMethod(urlPostfix, data)}
                      afterSubmit={{
                        execute: (data: TimelineItem) => {
                          addToList(wrapItem(data, source));
                          closeAddForm();
                          if (additionalAfterAddSubmit) {
                            additionalAfterAddSubmit(data, parentContext, reloadData);
                          }
                        },
                      }}
                    />
                  );
                  setAddForm(form);
                }}
              />
            )}
            {additionalButtons}
          </Box>
        </Box>
      </Box>
      <Box>{children}</Box>
    </>
  );
};

export default PersonTimelinePageLayout;
